import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    FaTimes,
    FaSearch,
    FaUsers,
    FaUser,
    FaEnvelope,
    FaMapMarkerAlt,
    FaPhone,
    FaTags,
    FaCheckCircle,
    FaTimesCircle,
    FaUserCheck,
    FaGlobe,
    FaCalendar,
    FaFilter,
    FaSort,
    FaPlus,
    FaEdit,
    FaTrash,
    FaExclamationTriangle,
    FaUnlink
} from 'react-icons/fa';
import styles from './ClientContactsModal.module.css';
import { 
    vwClientContactsSummaryService, 
    VWClientContactsSummaryHelper 
} from '../../services/vwClientContactsSummaryService';
import { contactService } from '../../services/contactService';
import { contactTypeTagsService } from '../../services/contactTypeTagsService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useAlert } from '../../context/AlertContext';
import AddContactModal from './AddContactModal';
import EditContactModal from './EditContactModal';

/**
 * Status Badge Component
 * Displays status badges for a contact (Active, Subscribed, Portal Access)
 */
const StatusBadge = ({ label, type }) => {
    const badgeClass = `${styles.statusBadge} ${styles[type] || ''}`;
    return <span className={badgeClass}>{label}</span>;
};

/**
 * Contact Tag Component
 * Displays a single contact type tag
 */
const ContactTag = ({ tag }) => {
    return (
        <span className={styles.contactTag} title={tag.Description}>
            {tag.ContactType}
        </span>
    );
};

/**
 * Contact Card Component
 * Displays individual contact information in a card format
 */
const ContactCard = ({ contact, helpers, onEdit, onDelete, onRemoveFromClient, loading }) => {
    const tags = helpers.parseTagsJson(contact.tagsJson);
    const badges = helpers.getStatusBadges(contact);
    const fullAddress = helpers.getFullAddress(contact);
    const relativeTime = helpers.getRelativeTime(contact.contactCreatedAt);

    return (
        <div className={styles.contactCard}>
            {/* Header: Name and Status */}
            <div className={styles.contactHeader}>
                <div className={styles.contactIcon}>
                    <FaUser />
                </div>
                <div className={styles.contactInfo}>
                    <h4 className={styles.contactName}>{helpers.getFullName(contact)}</h4>
                    <div className={styles.statusBadges}>
                        {badges.map((badge, idx) => (
                            <StatusBadge key={idx} label={badge.label} type={badge.type} />
                        ))}
                    </div>
                </div>
                {/* Action Buttons */}
                <div className={styles.contactActions}>
                    <button
                        className={styles.actionButton}
                        onClick={() => onEdit && onEdit(contact)}
                        disabled={loading}
                        title="Edit contact"
                    >
                        <FaEdit />
                    </button>
                    <button
                        className={`${styles.actionButton} ${styles.unlinkButton}`}
                        onClick={() => onRemoveFromClient && onRemoveFromClient(contact)}
                        disabled={loading}
                        title="Remove contact from this client"
                    >
                        <FaUnlink />
                    </button>
                    <button
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        onClick={() => onDelete && onDelete(contact)}
                        disabled={loading}
                        title="Delete contact permanently"
                    >
                        <FaTrash />
                    </button>
                </div>
            </div>

            {/* Contact Details */}
            <div className={styles.contactDetails}>
                {/* Email */}
                {contact.email && (
                    <div className={styles.detailRow}>
                        <FaEnvelope className={styles.detailIcon} />
                        <a href={`mailto:${contact.email}`} className={styles.emailLink}>
                            {contact.email}
                        </a>
                    </div>
                )}

                {/* Phone */}
                {(contact.phone || contact.cellPhone || contact.workPhone) && (
                    <div className={styles.detailRow}>
                        <FaPhone className={styles.detailIcon} />
                        <span className={styles.detailText}>
                            {contact.phone || contact.cellPhone || contact.workPhone}
                        </span>
                    </div>
                )}

                {/* Address */}
                {(contact.address1 || contact.city) && (
                    <div className={styles.detailRow}>
                        <FaMapMarkerAlt className={styles.detailIcon} />
                        <span className={styles.detailText}>{fullAddress}</span>
                    </div>
                )}

                {/* Created Date */}
                {contact.contactCreatedAt && (
                    <div className={styles.detailRow}>
                        <FaCalendar className={styles.detailIcon} />
                        <span className={styles.detailText}>
                            Created {relativeTime}
                        </span>
                    </div>
                )}
            </div>

            {/* Contact Type Tags */}
            {tags.length > 0 && (
                <div className={styles.contactTags}>
                    <div className={styles.tagsLabel}>
                        <FaTags className={styles.tagsIcon} />
                        <span>Contact Types ({tags.length})</span>
                    </div>
                    <div className={styles.tagsList}>
                        {tags.map((tag) => (
                            <ContactTag key={tag.ContactTypeID} tag={tag} />
                        ))}
                    </div>
                </div>
            )}

            {/* Last Tagged Date */}
            {contact.lastTaggedAt && (
                <div className={styles.lastTagged}>
                    <small>Last tagged: {helpers.formatDate(contact.lastTaggedAt)}</small>
                </div>
            )}
        </div>
    );
};

/**
 * Filter Dropdown Component
 * Provides filtering options for contacts
 */
const FilterDropdown = ({ currentFilter, onFilterChange, statistics }) => {
    return (
        <div className={styles.filterDropdown}>
            <label htmlFor="contactFilter" className={styles.filterLabel}>
                <FaFilter /> Filter:
            </label>
            <select
                id="contactFilter"
                className={styles.filterSelect}
                value={currentFilter}
                onChange={(e) => onFilterChange(e.target.value)}
            >
                <option value="all">All Contacts ({statistics.total})</option>
                <option value="active">Active Only ({statistics.active})</option>
                <option value="inactive">Inactive Only ({statistics.inactive})</option>
                <option value="subscribed">Subscribed ({statistics.subscribed})</option>
                <option value="portal">Portal Access ({statistics.withPortalAccess})</option>
                <option value="with-tags">With Tags ({statistics.withTags})</option>
            </select>
        </div>
    );
};

/**
 * Sort Dropdown Component
 * Provides sorting options for contacts
 */
const SortDropdown = ({ currentSort, onSortChange }) => {
    return (
        <div className={styles.sortDropdown}>
            <label htmlFor="contactSort" className={styles.sortLabel}>
                <FaSort /> Sort by:
            </label>
            <select
                id="contactSort"
                className={styles.sortSelect}
                value={currentSort}
                onChange={(e) => onSortChange(e.target.value)}
            >
                <option value="name">Full Name</option>
                <option value="lastName">Last Name</option>
                <option value="email">Email</option>
                <option value="tagCount">Tag Count</option>
                <option value="lastTagged">Last Tagged</option>
                <option value="created">Created Date</option>
            </select>
        </div>
    );
};

/**
 * Delete Confirmation Dialog
 */
const DeleteConfirmDialog = ({ contact, isOpen, onClose, onConfirm, deleting }) => {
    if (!isOpen || !contact) return null;

    const contactName = VWClientContactsSummaryHelper.getFullName(contact);

    return (
        <div className={styles.dialogOverlay} onClick={onClose}>
            <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.dialogHeader}>
                    <h3>Delete Contact</h3>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={deleting}
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className={styles.dialogBody}>
                    <div className={styles.warningIcon}>
                        <FaExclamationTriangle size={48} color="#e74c3c" />
                    </div>
                    <p className={styles.confirmMessage}>
                        Are you sure you want to delete this contact? This action cannot be undone.
                    </p>
                    <div className={styles.contactPreview}>
                        <p><strong>Name:</strong> {contactName}</p>
                        {contact.email && <p><strong>Email:</strong> {contact.email}</p>}
                        {contact.contactCreatedAt && (
                            <p><strong>Created:</strong> {VWClientContactsSummaryHelper.formatDate(contact.contactCreatedAt)}</p>
                        )}
                    </div>
                </div>
                <div className={styles.dialogActions}>
                    <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={onClose}
                        disabled={deleting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.btnDanger}
                        onClick={onConfirm}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete Contact'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Remove from Client Confirmation Dialog
 */
const RemoveFromClientDialog = ({ contact, isOpen, onClose, onConfirm, removing, clientName }) => {
    if (!isOpen || !contact) return null;

    const contactName = VWClientContactsSummaryHelper.getFullName(contact);

    return (
        <div className={styles.dialogOverlay} onClick={onClose}>
            <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.dialogHeader}>
                    <h3>Remove Contact from Client</h3>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={removing}
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className={styles.dialogBody}>
                    <div className={styles.warningIcon}>
                        <FaUnlink size={48} color="#ff9800" />
                    </div>
                    <p className={styles.confirmMessage}>
                        Are you sure you want to remove this contact from the client? The contact will not be deleted, only unlinked from this client.
                    </p>
                    <div className={styles.contactPreview}>
                        <p><strong>Contact:</strong> {contactName}</p>
                        {contact.email && <p><strong>Email:</strong> {contact.email}</p>}
                        <p><strong>Client:</strong> {clientName}</p>
                    </div>
                </div>
                <div className={styles.dialogActions}>
                    <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={onClose}
                        disabled={removing}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.btnWarning}
                        onClick={onConfirm}
                        disabled={removing}
                    >
                        {removing ? 'Removing...' : 'Remove from Client'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Client Contacts Modal Component
 * Modal popup for displaying client contacts with search, filter, and sort functionality
 */
const ClientContactsModal = ({ clientId, clientName, clientCode, isOpen, onClose, onDataChanged }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert, setIsModalOpen } = useAlert();

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // State management
    const [contacts, setContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBy, setFilterBy] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [statistics, setStatistics] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        subscribed: 0,
        withPortalAccess: 0,
        withTags: 0,
        withoutTags: 0,
        uniqueTags: []
    });

    // Add Contact Modal state
    const [showAddContactModal, setShowAddContactModal] = useState(false);

    // Edit Contact Modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState(null);

    // Delete Contact Dialog state
    const [deletingContact, setDeletingContact] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    // Remove from Client Dialog state
    const [removingContact, setRemovingContact] = useState(null);
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);

    // Track previous open state to only load when transitioning from closed to open
    const prevIsOpenRef = useRef(false);

    /**
     * Load contacts for the client
     */
    const loadContacts = useCallback(async () => {
        if (!clientId) return;

        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();

            // Fetch contacts based on current filter
            let fetchedContacts;
            switch (filterBy) {
                case 'active':
                    fetchedContacts = await vwClientContactsSummaryService.getActiveContacts(accessToken, clientId);
                    break;
                case 'inactive':
                    fetchedContacts = await vwClientContactsSummaryService.getInactiveContacts(accessToken, clientId);
                    break;
                case 'subscribed':
                    fetchedContacts = await vwClientContactsSummaryService.getSubscribedContacts(accessToken, clientId);
                    break;
                case 'portal':
                    fetchedContacts = await vwClientContactsSummaryService.getContactsWithPortalAccess(accessToken, clientId);
                    break;
                case 'with-tags':
                    fetchedContacts = await vwClientContactsSummaryService.getContactsWithTags(accessToken, clientId);
                    break;
                default:
                    fetchedContacts = await vwClientContactsSummaryService.getContactsByClientId(accessToken, clientId);
            }

            setContacts(fetchedContacts);
            
            // Calculate statistics
            const stats = VWClientContactsSummaryHelper.getContactStatistics(fetchedContacts);
            setStatistics(stats);
        } catch (error) {
            console.error('Failed to load client contacts:', error);
            setErrorAlert('Failed to load contacts. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [clientId, filterBy, getBackendAccessToken, setErrorAlert]);

    /**
     * Filter and sort contacts based on current criteria
     */
    useEffect(() => {
        let filtered = [...contacts];

        // Apply search filter
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase();
            filtered = filtered.filter(contact =>
                contact.fullName?.toLowerCase().includes(query) ||
                contact.firstName?.toLowerCase().includes(query) ||
                contact.lastName?.toLowerCase().includes(query) ||
                contact.email?.toLowerCase().includes(query)
            );
        }

        // Apply sorting
        filtered = VWClientContactsSummaryHelper.sortContacts(filtered, sortBy, 'asc');

        setFilteredContacts(filtered);
    }, [searchTerm, sortBy, contacts]);

    /**
     * Load contacts only when modal opens (not when it closes)
     * Only load when transitioning from closed to open, not on every render
     */
    useEffect(() => {
        // Only load when modal transitions from closed to open
        const wasClosedNowOpen = !prevIsOpenRef.current && isOpen;
        
        if (wasClosedNowOpen && clientId) {
            loadContacts();
        }
        
        // Update the ref for next render
        prevIsOpenRef.current = isOpen;
    }, [isOpen, clientId, loadContacts]);

    /**
     * Notify AlertContext when modal opens/closes
     * Also set body attribute to block header interactions
     */
    useEffect(() => {
        if (isOpen) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }

        // Cleanup on unmount
        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [isOpen, setIsModalOpen]);

    /**
     * Handle search input change
     */
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    /**
     * Handle filter change
     */
    const handleFilterChange = (newFilter) => {
        setFilterBy(newFilter);
        setSearchTerm(''); // Clear search when filter changes
    };

    /**
     * Handle sort change
     */
    const handleSortChange = (newSort) => {
        setSortBy(newSort);
    };

    /**
     * Handle edit contact
     */
    const handleEditContact = (contact) => {
        setEditingContact(contact);
        setEditModalOpen(true);
    };

    /**
     * Handle delete contact - show confirmation dialog
     */
    const handleDeleteContact = (contact) => {
        setDeletingContact(contact);
        setShowDeleteDialog(true);
    };

    /**
     * Handle remove contact from client - show confirmation dialog
     */
    const handleRemoveFromClient = (contact) => {
        setRemovingContact(contact);
        setShowRemoveDialog(true);
    };

    /**
     * Confirm delete contact
     */
    const handleConfirmDelete = async () => {
        if (!deletingContact) return;

        const contactName = VWClientContactsSummaryHelper.getFullName(deletingContact);
        
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            await contactService.deleteContact(accessToken, deletingContact.contactId);
            
            setInfoAlert(`Contact "${contactName}" has been successfully deleted`);
            setShowDeleteDialog(false);
            setDeletingContact(null);
            await loadContacts(); // Reload the contacts list
            
            // Notify parent that data has changed (refresh client list in background)
            if (onDataChanged) {
                onDataChanged();
            }
        } catch (error) {
            console.error('Failed to delete contact:', error);
            setErrorAlert(`Failed to delete contact "${contactName}". Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Confirm remove contact from client
     */
    const handleConfirmRemove = async () => {
        if (!removingContact) return;

        const contactName = VWClientContactsSummaryHelper.getFullName(removingContact);
        
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Remove contact from client by deleting all contact type tags for this client-contact pair
            // This unlinks the contact from the client without deleting the contact itself
            await contactTypeTagsService.deleteTagsForClientContact(
                accessToken, 
                clientId, 
                removingContact.contactId
            );
            
            setInfoAlert(`Contact "${contactName}" has been removed from this client`);
            setShowRemoveDialog(false);
            setRemovingContact(null);
            await loadContacts(); // Reload the contacts list
            
            // Notify parent that data has changed (refresh client list in background)
            if (onDataChanged) {
                onDataChanged();
            }
        } catch (error) {
            console.error('Failed to remove contact from client:', error);
            setErrorAlert(`Failed to remove contact "${contactName}" from this client. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle contact added successfully
     */
    const handleContactAdded = useCallback(() => {
        // Reload contacts to show the newly added contact
        loadContacts();
        setInfoAlert('Contact added successfully!');
        
        // Notify parent that data has changed (refresh client list in background)
        if (onDataChanged) {
            onDataChanged();
        }
    }, [loadContacts, setInfoAlert, onDataChanged]);

    /**
     * Handle contact updated from edit modal
     */
    const handleContactUpdated = async (updatedContact, wasDeleted = false) => {
        if (wasDeleted) {
            // Contact was deleted, reload the contacts list
            await loadContacts();
        } else if (updatedContact) {
            // Contact was updated, reload the contacts list to get fresh data
            await loadContacts();
        }
        
        // Notify parent that data has changed (refresh client list in background)
        if (onDataChanged) {
            onDataChanged();
        }
        
        // Close the modal
        setEditModalOpen(false);
        setEditingContact(null);
    };

    /**
     * Handle close edit modal
     */
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setEditingContact(null);
    };

    /**
     * Handle modal close
     */
    const handleClose = () => {
        // Reset state
        setSearchTerm('');
        setFilterBy('all');
        setSortBy('name');
        setContacts([]);
        setFilteredContacts([]);
        onClose();
    };

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <div className={styles.headerIcon}>
                            <FaUsers />
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>Client Contacts</h2>
                            <p className={styles.modalSubtitle}>
                                {clientCode && `${clientCode} - `}
                                {clientName || 'Unknown Client'}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        title="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Controls Section */}
                <div className={styles.controlsSection}>
                    {/* Search Bar and Add Contact Button */}
                    <div className={styles.topControls}>
                        <div className={styles.searchContainer}>
                            <div className={styles.searchInputWrapper}>
                                <FaSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Search contacts by name or email..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                                {searchTerm && (
                                    <button
                                        className={styles.clearSearchButton}
                                        onClick={() => setSearchTerm('')}
                                        title="Clear search"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Add Contact Button */}
                        <button
                            className={styles.addContactButton}
                            onClick={() => setShowAddContactModal(true)}
                            title="Add new contact"
                        >
                            <FaPlus /> Add Contact
                        </button>
                    </div>

                    {/* Filter and Sort Controls */}
                    <div className={styles.filtersRow}>
                        <FilterDropdown
                            currentFilter={filterBy}
                            onFilterChange={handleFilterChange}
                            statistics={statistics}
                        />
                        <SortDropdown
                            currentSort={sortBy}
                            onSortChange={handleSortChange}
                        />
                    </div>

                    {/* Results Count */}
                    <div className={styles.resultsCount}>
                        {loading ? (
                            'Loading contacts...'
                        ) : (
                            `Showing ${filteredContacts.length} of ${contacts.length} contacts`
                        )}
                    </div>
                </div>

                {/* Contacts List */}
                <div className={styles.contactsContainer}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <FaUsers className={styles.loadingIcon} />
                            <p>Loading contacts...</p>
                        </div>
                    ) : filteredContacts.length === 0 ? (
                        <div className={styles.emptyState}>
                            <FaUser className={styles.emptyIcon} />
                            <p>
                                {searchTerm || filterBy !== 'all'
                                    ? 'No contacts found matching your criteria.'
                                    : 'No contacts found for this client.'}
                            </p>
                        </div>
                    ) : (
                        <div className={styles.contactsGrid}>
                            {filteredContacts.map((contact) => (
                                <ContactCard
                                    key={`${contact.clientId}-${contact.contactId}`}
                                    contact={contact}
                                    helpers={VWClientContactsSummaryHelper}
                                    onEdit={handleEditContact}
                                    onDelete={handleDeleteContact}
                                    onRemoveFromClient={handleRemoveFromClient}
                                    loading={loading}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add Contact Modal */}
            <AddContactModal
                clientId={clientId}
                clientName={clientName}
                clientCode={clientCode}
                isOpen={showAddContactModal}
                onClose={() => setShowAddContactModal(false)}
                onContactAdded={handleContactAdded}
            />

            {/* Edit Contact Modal */}
            <EditContactModal
                contact={editingContact}
                isOpen={editModalOpen}
                onClose={handleCloseEditModal}
                onContactUpdated={handleContactUpdated}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                contact={deletingContact}
                isOpen={showDeleteDialog}
                onClose={() => {
                    setShowDeleteDialog(false);
                    setDeletingContact(null);
                }}
                onConfirm={handleConfirmDelete}
                deleting={loading}
            />

            {/* Remove from Client Confirmation Dialog */}
            <RemoveFromClientDialog
                contact={removingContact}
                isOpen={showRemoveDialog}
                onClose={() => {
                    setShowRemoveDialog(false);
                    setRemovingContact(null);
                }}
                onConfirm={handleConfirmRemove}
                removing={loading}
                clientName={clientName}
            />
        </div>
    );
};

export default ClientContactsModal;
